import {DataTable} from "../DataTable/DataTable";
import {RrcEp10WasteManifestEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {
  useRrcEp10WasteManifestDataTableFilter
} from "../RrcEp10WasteManifestDataTable/useRrcEp10WasteManifestDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {useNavigate} from "react-router-dom";
import {useRrcEp10WasteManifestAction} from "../../menu/RrcEp10WasteManifestActionMenu/useRrcEp10WasteManifestAction";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

const EMPTY_ARRAY: RrcEp10WasteManifestEntity[] = []

export type RrcEp10WasteManifestDataTableProps = EntityDataTableProps<RrcEp10WasteManifestEntity> & {
  viewNav?: "by_key" | "by_id"
}

export const RrcEp10WasteManifestDataTable = (props: RrcEp10WasteManifestDataTableProps) => {
  const {
    records: rrcEp10WasteManifests = EMPTY_ARRAY,
    recordsPerPageOptions,
    viewNav = "by_key",
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useRrcEp10WasteManifestDataTableFilter({
    records: rrcEp10WasteManifests
  })

  const sortConfig = useMemo(() => {
    return {
      "rrcDistrict": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.rrcDistrict?.toLowerCase()
          }])
        }
      },
      "facilityName": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.facilityName?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'rrcDistrict', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: RrcEp10WasteManifestEntity) => {
    e.stopPropagation()
    let postfix = viewNav === "by_key" ? `key-profile?key=${record.manifestNumber}` : `profile?id=${record.id}`
    if (record.tenantCode) {
      postfix += `&tenantCode=${record.tenantCode}`
    }
    navigate(`/compliance/rrc/ep10/${postfix}`)
  }, [navigate, viewNav])

  const columns = useMemo(() => {
    const values: DataTableColumn<RrcEp10WasteManifestEntity>[] = [
      {
        title: t("common.entity.rrcep10wastemanifest.table.column.number", "Manifest Number"),
        accessor: 'manifestNumber',
        width: 75,
        sortable: true,
      },
      {
        title: t("common.entity.rrcep10wastemanifest.table.column.status", "Status"),
        accessor: 'status',
        width: 75,
        sortable: true,
      },
      {
        title: t("common.entity.rrcep10wastemanifest.table.column.version", "Version"),
        accessor: 'version',
        width: 45,
        sortable: true,
      },
      {
        title: '',
        accessor: "actions",
        width: 65,
        textAlignment: 'right',
        render: (rrcEp10WasteManifest) => (
          <EntityActionGroup
            permissionPrefix="rrcep10wastemanifest"
            edit={false}
            delete={false}
            onViewActionClick={(e) => onViewActionClick(e, rrcEp10WasteManifest)}/>
        )
      }
    ]

    return values
  }, [filterConfig, onViewActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
